import React from 'react';
import './LeadDetailsDrawer.css';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import { useDispatch, useSelector } from 'react-redux';

import LeadSocialIcons from './LeadSocialIcons';
import { revealLeads } from '../../../store/Leads/index';

const LeadDetailsDrawer = ({ open, onClose, lead }) => {
  const dispatch = useDispatch();
  const { revealedData, revealingIds } = useSelector(s => s['Leads']);

  if (!lead) return null;

  const id = lead['id'] || lead['_id'];
  const revealed = revealedData[id] || {};
  const isRevealing = revealingIds[id];
  const isRevealed = Boolean(revealedData[id]);

  const handleReveal = (e) => {
    e.stopPropagation();
    dispatch(revealLeads({ prospectIds: [id], fields: ['email', 'phone', 'linkedin'] }));
  };

  const merged = { ...lead, ...revealed };

  const addressParts = [merged['address1'], merged['address2'], merged['city'], merged['state'], merged['zip'], merged['country']].filter(Boolean);
  const fullAddress = addressParts.join(', ');

  const technologies = merged['technologies'] ? merged['technologies'].split(',').map(t => t.trim()).filter(Boolean) : [];
  const tags = merged['tags'] ? merged['tags'].split(',').map(t => t.trim()).filter(Boolean) : [];

  const fullName = `${merged['firstName'] || ''} ${merged['lastName'] || ''}`.trim() || '—';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="lead-details-drawer"
      PaperProps={{ className: 'lead-details-drawer-paper' }}
    >
      <div className="lead-details-drawer-content">

        {/* Header */}
        <div className="lead-details-drawer-header">
          <div className="lead-details-drawer-header-info">
            <div className="lead-details-drawer-avatar">
              {(merged['firstName'] || '?').charAt(0).toUpperCase()}
            </div>
            <div className="lead-details-drawer-header-text">
              <div className="lead-details-drawer-name">{fullName}</div>
              {merged['jobTitle'] && <div className="lead-details-drawer-title">{merged['jobTitle']}</div>}
            </div>
          </div>
          <div className="lead-details-drawer-header-actions">
            {!isRevealed && (
              <button className="lead-details-drawer-reveal-pill" onClick={handleReveal} disabled={isRevealing}>
                {isRevealing ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-unlock-alt" />}
                Reveal
              </button>
            )}
            <button className="lead-details-drawer-close" onClick={onClose}>
              <i className="fa fa-times" />
            </button>
          </div>
        </div>

        <div className="lead-details-drawer-body">

          {/* Person */}
          <div className="lead-details-drawer-section">
            <div className="lead-details-drawer-section-title">Person</div>
            <div className="lead-details-drawer-grid">
              <Cell label="Email" value={isRevealed ? (revealed['email'] || null) : null} locked={!isRevealed} hasData={lead['hasEmail']} wide />
              <Cell label="Phone" value={isRevealed ? (revealed['phone'] || null) : null} locked={!isRevealed} hasData={lead['hasPhone']} />
              {isRevealed && revealed['phone2'] && <Cell label="Phone 2" value={revealed['phone2']} />}
              {isRevealed && revealed['workLine'] && <Cell label="Work Line" value={`${revealed['workLine']}${revealed['ext'] ? ` ext. ${revealed['ext']}` : ''}`} />}
              {isRevealed && revealed['linkedinUrl']
                ? <Cell label="LinkedIn" url={revealed['linkedinUrl']} wide />
                : !isRevealed && lead['hasLinkedin'] && <Cell label="LinkedIn" locked hasData />
              }
              {merged['department'] && <Cell label="Department" value={merged['department']} />}
              {merged['employeeCountry'] && <Cell label="Country" value={merged['employeeCountry']} />}
              {merged['startDate'] && <Cell label="Start Date" value={merged['startDate']} />}
              {merged['lastVerified'] && <Cell label="Last Verified" value={merged['lastVerified']} />}
              {merged['phoneLineType'] && <Cell label="Line Type" value={merged['phoneLineType']} />}
              {merged['emailVerificationResult'] && <Cell label="Email Status" value={merged['emailVerificationResult']} />}
            </div>
          </div>

          {/* Company */}
          <div className="lead-details-drawer-section">
            <div className="lead-details-drawer-section-title">Company</div>
            <div className="lead-details-drawer-grid">
              {merged['companyName'] && <Cell label="Name" value={merged['companyName']} wide />}
              {merged['companyDomain'] && <Cell label="Website" url={`https://${merged['companyDomain']}`} wide />}
              {merged['industry'] && <Cell label="Industry" value={merged['industry']} />}
              {merged['revenue'] && <Cell label="Revenue" value={merged['revenue']} />}
              {merged['employeeCount'] && <Cell label="Employees" value={Number(merged['employeeCount']).toLocaleString()} />}
              {merged['linkedinEmployeeCount'] > 0 && <Cell label="LI Employees" value={Number(merged['linkedinEmployeeCount']).toLocaleString()} />}
              {merged['companyFounded'] && <Cell label="Founded" value={merged['companyFounded']} />}
              {merged['hqPhone'] && <Cell label="HQ Phone" value={merged['hqPhone']} />}
              {merged['companyLeadScore'] > 0 && <Cell label="Lead Score" value={merged['companyLeadScore']} />}
              {merged['sicCodes'] && <Cell label="SIC" value={merged['sicCodes']} />}
              {merged['sicDescription'] && <Cell label="SIC Desc." value={merged['sicDescription']} wide />}
              {merged['companyIndustries'] && <Cell label="Sub-industries" value={merged['companyIndustries']} wide />}
              {fullAddress && <Cell label="Address" value={fullAddress} wide />}
            </div>
            {merged['companyBio'] && (
              <div className="lead-details-drawer-bio-row">
                <span className="lead-details-drawer-bio-label">About</span>
                <span className="lead-details-drawer-field-bio">{merged['companyBio']}</span>
              </div>
            )}
          </div>

          {/* Tech & Tags */}
          {(technologies.length > 0 || tags.length > 0) && (
            <div className="lead-details-drawer-section">
              <div className="lead-details-drawer-section-title">Tech &amp; Tags</div>
              {tags.length > 0 && (
                <div className="lead-details-drawer-tags-row">
                  <span className="lead-details-drawer-cell-label">Tags</span>
                  <div className="lead-details-drawer-chips">
                    {tags.map(t => <span key={t} className="lead-details-drawer-chip lead-details-drawer-chip--tag">{t}</span>)}
                  </div>
                </div>
              )}
              {technologies.length > 0 && (
                <div className="lead-details-drawer-tags-row">
                  <span className="lead-details-drawer-cell-label">Technologies</span>
                  <div className="lead-details-drawer-chips">
                    {technologies.map(t => <span key={t} className="lead-details-drawer-chip">{t}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Company Socials */}
          {(merged['companyLinkedin'] || merged['companyTwitter'] || merged['companyFacebook'] || merged['companyInstagram'] || merged['companyYoutube'] || merged['companyWikipedia']) && (
            <div className="lead-details-drawer-section">
              <div className="lead-details-drawer-section-title">Company Socials</div>
              <LeadSocialIcons lead={merged} type="company" className="lead-details-drawer-social-row" />
            </div>
          )}

          {/* Data quality / meta */}
          {(merged['dataQuality'] || merged['dataSource']) && (
            <div className="lead-details-drawer-section">
              <div className="lead-details-drawer-section-title">Data Quality</div>
              <div className="lead-details-drawer-grid">
                {merged['dataSource'] && <Cell label="Source" value={merged['dataSource']} />}
                {merged['dataQuality'] && (
                  <div className="lead-details-drawer-cell lead-details-drawer-cell--wide">
                    <span className="lead-details-drawer-cell-label">Quality Score</span>
                    <div className="lead-details-drawer-quality">
                      <div className="lead-details-drawer-quality-bar">
                        <div className="lead-details-drawer-quality-fill" style={{ width: `${Math.min(100, merged['dataQuality'])}%` }} />
                      </div>
                      <span className="lead-details-drawer-quality-val">{merged['dataQuality']}/100</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </Drawer>
  );
};

// wide=true spans both columns in the grid
const Cell = ({ label, value, url, locked, hasData, wide }) => {
  if (locked) {
    if (!hasData) return null;
    return (
      <div className={`lead-details-drawer-cell${wide ? ' lead-details-drawer-cell--wide' : ''}`}>
        <span className="lead-details-drawer-cell-label">{label}</span>
        <span className="lead-details-drawer-field-locked"><i className="fa fa-lock" /> Reveal</span>
      </div>
    );
  }
  if (url) return (
    <div className={`lead-details-drawer-cell${wide ? ' lead-details-drawer-cell--wide' : ''}`}>
      <span className="lead-details-drawer-cell-label">{label}</span>
      <a className="lead-details-drawer-field-url" href={url} target="_blank" rel="noreferrer">{url.replace(/^https?:\/\/(www\.)?/, '')}</a>
    </div>
  );
  if (!value && value !== 0) return null;
  return (
    <div className={`lead-details-drawer-cell${wide ? ' lead-details-drawer-cell--wide' : ''}`}>
      <span className="lead-details-drawer-cell-label">{label}</span>
      <span className="lead-details-drawer-field-value">{value}</span>
    </div>
  );
};

export default LeadDetailsDrawer;
